import React, { useMemo, useState } from "react";
import { Box, Checkbox, Collapse, FormControlLabel, IconButton, Paper, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { ClassSubjectsData } from "../../academicOperations/types";
import { assignmentKey } from "../hooks/useFacultyForm";

type Props = {
  tree: ClassSubjectsData;
  ticked: Set<string>;
  onToggle: (keys: string[]) => void;
  disabled?: boolean;
};

/**
 * Grade → section → subject checkboxes. Only subjects are saved; grade and section boxes are
 * shortcuts that tick/untick everything under them ("–" when only part is ticked).
 */
const AssignmentTree: React.FC<Props> = ({ tree, ticked, onToggle, disabled }) => {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const subjectName = useMemo(
    () => Object.fromEntries(tree.subjects.map((subject) => [subject.code, subject.name])),
    [tree.subjects]
  );

  const state = (keys: string[]) => {
    const count = keys.filter((key) => ticked.has(key)).length;
    return { checked: keys.length > 0 && count === keys.length, indeterminate: count > 0 && count < keys.length };
  };

  const toggleExpanded = (gradeId: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(gradeId)) next.delete(gradeId);
      else next.add(gradeId);
      return next;
    });

  if (tree.grades.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No grades yet. Set them up under Academics first.
      </Typography>
    );
  }

  return (
    <Paper variant="outlined" sx={{ borderRadius: 1.5, maxHeight: 420, overflowY: "auto" }}>
      {tree.grades.map((grade) => {
        const sectionKeys = grade.sections.map((section) =>
          section.subjectCodes.map((code) => assignmentKey({ gradeSectionId: section.gradeSectionId, subjectCode: code }))
        );
        const gradeKeys = sectionKeys.flat();
        const gradeState = state(gradeKeys);
        const open = expanded.has(grade.id);

        return (
          <Box key={grade.id} sx={{ borderBottom: 1, borderColor: "divider", "&:last-child": { borderBottom: 0 } }}>
            <Box display="flex" alignItems="center" px={1}>
              <IconButton size="small" onClick={() => toggleExpanded(grade.id)} aria-label={`expand ${grade.name}`}>
                {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
              </IconButton>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={gradeState.checked}
                    indeterminate={gradeState.indeterminate}
                    onChange={() => onToggle(gradeKeys)}
                    disabled={disabled || gradeKeys.length === 0}
                  />
                }
                label={<Typography fontWeight={600}>{grade.name}</Typography>}
              />
              {gradeKeys.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  {grade.sections.length === 0 ? "no sections" : "no subjects"}
                </Typography>
              )}
            </Box>

            <Collapse in={open} unmountOnExit>
              <Box pl={6} pr={2} pb={1}>
                {grade.sections.map((section, index) => {
                  const keys = sectionKeys[index];
                  const sectionState = state(keys);
                  return (
                    <Box key={section.gradeSectionId} display="flex" alignItems="center" flexWrap="wrap" columnGap={1}>
                      <FormControlLabel
                        sx={{ minWidth: 90 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={sectionState.checked}
                            indeterminate={sectionState.indeterminate}
                            onChange={() => onToggle(keys)}
                            disabled={disabled || keys.length === 0}
                          />
                        }
                        label={section.name}
                      />
                      {section.subjectCodes.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          No subjects — add them in Class Subjects.
                        </Typography>
                      ) : (
                        section.subjectCodes.map((code) => {
                          const key = assignmentKey({ gradeSectionId: section.gradeSectionId, subjectCode: code });
                          return (
                            <FormControlLabel
                              key={key}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={ticked.has(key)}
                                  onChange={() => onToggle([key])}
                                  disabled={disabled}
                                />
                              }
                              label={subjectName[code] ?? code}
                            />
                          );
                        })
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Paper>
  );
};

export default AssignmentTree;
